import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Copy, Sparkles, Loader2, MessageSquareCode, ArrowRight, Library } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  category: "linkedin_post",
  task_description: "",
  audience: "",
  voice_tone: "",
  format_constraints: "",
  length: "",
  extra_context: "",
  target_llm: "ChatGPT",
  ask_clarifying_questions: true,
  refine_step: true,
};

export default function TextAssistant() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/text-prompts/categories")
      .then(({ data }) => setCategories(data))
      .catch(() => { /* silent */ void 0; });
  }, []);

  // Prefill form from an existing prompt when navigated with ?edit=<id>
  useEffect(() => {
    const editId = params.get("edit");
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get("/text-prompts", { params: { limit: 200 } });
        const found = (data || []).find((p) => p.text_prompt_id === editId);
        if (found && found.input_data) {
          setForm({ ...EMPTY_FORM, ...found.input_data });
          toast.success(t("edit_loaded") || "Prompt loaded — modify and regenerate");
        }
      } catch { /* ignore */ }
      navigate("/app/text-assistant", { replace: true });
    })();
  }, []);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target?.value ?? e }));

  const generate = async () => {
    if (form.task_description.trim().length < 5) {
      toast.error(t("txt_task_required") || "Task description is too short");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/text-prompts/generate", form);
      setResult(data);
      toast.success(t("txt_generated") || "Prompt generated");
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.generated_prompt);
      toast.success(t("copied") || "Copied");
    } catch { toast.error("Clipboard blocked"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-4xl md:text-5xl font-black text-white flex items-center gap-3">
            <MessageSquareCode className="w-10 h-10 text-sky-400" />
            {t("txt_title") || "AI Prompt Assistant"}
          </h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">
            {t("txt_subtitle") || "Build ready-to-paste prompts for ChatGPT, Claude, Gemini and any LLM. Applies the 'Great Prompt' framework: role, context, clear task, constraints and clarifying questions."}
          </p>
        </div>
        <Link to="/app/text-library" data-testid="txt-open-library" className="text-sm text-orange-400 hover:text-orange-300 inline-flex items-center gap-1">
          <Library className="w-4 h-4" /> {t("nav_text_library") || "Library"} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Left: form */}
        <div className="space-y-4 bg-white/[0.02] border border-white/10 rounded-md p-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_category") || "Category"}</Label>
            <select
              value={form.category}
              onChange={update("category")}
              data-testid="txt-category-select"
              className="w-full mt-1 bg-[#0E0E12]/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/60"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_task") || "Task"} *</Label>
            <Textarea
              value={form.task_description}
              onChange={update("task_description")}
              placeholder={t("txt_task_ph") || "What should the LLM produce? e.g., Write a LinkedIn post about the value of personal branding for solopreneurs"}
              maxLength={800}
              rows={3}
              data-testid="txt-task-input"
              className="mt-1 bg-[#0E0E12]/40 border-white/10 text-sm"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_audience") || "Audience"}</Label>
            <Input
              value={form.audience}
              onChange={update("audience")}
              placeholder={t("txt_audience_ph") || "Who reads this? e.g., early-stage solopreneurs on LinkedIn"}
              maxLength={400}
              data-testid="txt-audience-input"
              className="mt-1 bg-[#0E0E12]/40 border-white/10 text-sm"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_voice") || "Voice & Tone"}</Label>
            <Input
              value={form.voice_tone}
              onChange={update("voice_tone")}
              placeholder={t("txt_voice_ph") || "e.g., punchy, direct, first-person, no jargon"}
              maxLength={300}
              data-testid="txt-voice-input"
              className="mt-1 bg-[#0E0E12]/40 border-white/10 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_length") || "Length"}</Label>
              <Input
                value={form.length}
                onChange={update("length")}
                placeholder={t("txt_length_ph") || "e.g., under 150 words"}
                maxLength={100}
                data-testid="txt-length-input"
                className="mt-1 bg-[#0E0E12]/40 border-white/10 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_llm") || "Target LLM"}</Label>
              <select
                value={form.target_llm}
                onChange={update("target_llm")}
                data-testid="txt-llm-select"
                className="w-full mt-1 bg-[#0E0E12]/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/60"
              >
                <option value="ChatGPT">ChatGPT</option>
                <option value="Claude">Claude</option>
                <option value="Gemini">Gemini</option>
                <option value="Any">Any (generic)</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_format") || "Format constraints"}</Label>
            <Input
              value={form.format_constraints}
              onChange={update("format_constraints")}
              placeholder={t("txt_format_ph") || "e.g., 3 options, bullet list, markdown headers, include CTA"}
              maxLength={300}
              data-testid="txt-format-input"
              className="mt-1 bg-[#0E0E12]/40 border-white/10 text-sm"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("txt_extra") || "Extra context"}</Label>
            <Textarea
              value={form.extra_context}
              onChange={update("extra_context")}
              placeholder={t("txt_extra_ph") || "Anything else the LLM should know? Examples of past work, KPIs, competitors, brand rules…"}
              maxLength={800}
              rows={3}
              data-testid="txt-extra-input"
              className="mt-1 bg-[#0E0E12]/40 border-white/10 text-sm"
            />
          </div>

          <div className="space-y-3 pt-2 border-t border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white font-medium">{t("txt_ask_q") || "Ask me clarifying questions first"}</div>
                <div className="text-xs text-zinc-500">{t("txt_ask_q_hint") || "The LLM will ask 3-6 questions before executing the task"}</div>
              </div>
              <Switch
                checked={form.ask_clarifying_questions}
                onCheckedChange={(v) => setForm((p) => ({ ...p, ask_clarifying_questions: v }))}
                data-testid="txt-ask-q-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white font-medium">{t("txt_refine") || "Refinement step"}</div>
                <div className="text-xs text-zinc-500">{t("txt_refine_hint") || "After you pick a winner, the LLM refines it once"}</div>
              </div>
              <Switch
                checked={form.refine_step}
                onCheckedChange={(v) => setForm((p) => ({ ...p, refine_step: v }))}
                data-testid="txt-refine-toggle"
              />
            </div>
          </div>

          <Button
            onClick={generate}
            disabled={busy || !user}
            data-testid="txt-generate-btn"
            className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-black font-bold"
          >
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("generating") || "Generating…"}</>) : (<><Sparkles className="w-4 h-4 mr-2" />{t("txt_generate") || "Generate prompt"}</>)}
          </Button>
        </div>

        {/* Right: output */}
        <div className="bg-white/[0.02] border border-white/10 rounded-md p-5 min-h-[300px] flex flex-col">
          {!result && !busy && (
            <div className="flex-1 flex items-center justify-center text-center text-zinc-500 text-sm">
              <div>
                <MessageSquareCode className="w-10 h-10 mx-auto mb-3 opacity-40" />
                {t("txt_empty") || "Your ready-to-paste LLM prompt will appear here"}
              </div>
            </div>
          )}
          {busy && (
            <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("generating") || "Generating…"}
            </div>
          )}
          {result && (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-widest text-sky-400 font-bold">{result.category_label}</span>
                <Button size="sm" variant="ghost" onClick={copy} data-testid="txt-copy-btn" className="text-zinc-400 hover:text-white">
                  <Copy className="w-4 h-4 mr-2" /> {t("copy") || "Copy"}
                </Button>
              </div>
              <pre
                className="flex-1 overflow-y-auto whitespace-pre-wrap break-words text-sm text-zinc-100 font-mono leading-relaxed bg-[#0E0E12]/40 border border-white/5 rounded p-4"
                data-testid="txt-output"
              >
                {result.generated_prompt}
              </pre>
              <div className="mt-3 text-xs text-zinc-500">
                {t("txt_saved_to_library") || "Saved to Assistant Library."}{" "}
                <Link to="/app/text-library" className="text-orange-400 hover:text-orange-300">{t("open_library") || "Open library"} →</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
